import React, { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import Divider from 'components/Divider'
import Drawer from 'components/Drawer'
import Paper from 'components/Paper'
import { useAppContext } from 'context/AppContext'
import { useEventContext } from 'context/EventContext'
import detachTask from 'helpers/detachTask'
import { getSelectedFilters } from 'helpers/filters'
import useViewport from 'hooks/useViewport'

import Facets from './components/Facets'
import FilterToggle from './components/FilterToggle'
import Header from './components/Header'
import StickyFilterToggle from './components/StickyFilterToggle'

import classes from './Filters.module.scss'

const RenderFilters = ({ facets, clearCallback, rootUrl, title, disableSPA, softSPA, softUpdate }) => (
  <>
    <Header
      facets={facets}
      clearCallback={clearCallback}
      rootUrl={rootUrl}
      title={title}
      disableSPA={disableSPA}
      softSPA={softSPA}
      softUpdate={softUpdate}
    />
    <Divider />
    <Facets facets={facets} disableSPA={disableSPA} softSPA={softSPA} softUpdate={softUpdate} />
  </>
)

/** @type {import("./types").default} */
const Filters = ({
  facets,
  rootUrl,
  title = 'Filter results',
  variant = 'temporary',
  stickyContainerRef = null, // optional reference to be sticking over
  topSpacing = 240, // optional space on top when in sticky behaviour
  isSticking = false,
  clearCallback = () => {},
  stickyFilterPosition,
  disableSPA = false,
  softSPA = false,
  softUpdate = () => {},
  drawerSeoFrendly = false,
  ...moreProps
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const defaultRef = useRef()
  const { isCypressTakingSnapshots } = useAppContext()
  const { up, down } = useViewport()
  const { sendEvent } = useEventContext()
  const isDrawer = variant !== 'permanent'
  const containerRef = stickyContainerRef || defaultRef
  const isListingPages = rootUrl && ['/alternatives', '/integrations'].some(str => rootUrl.includes(str))

  const upMd = up('md')
  const withStickyFilters = !isCypressTakingSnapshots && upMd

  const toggleDrawer = event => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return
    }
    const newDrawerIsOpen = !isDrawerOpen
    detachTask(setIsDrawerOpen, newDrawerIsOpen)
  }

  const filterToggle = stickyFilterPosition ? (
    <div className={clsx('StickyContainer', classes.stickyFilterContainer, stickyFilterPosition)}>
      <div
        className={clsx(
          isListingPages ? classes.stickyFilterForListingPages : classes.stickyFilter,
          !isCypressTakingSnapshots && classes.isSticky
        )}
      >
        <StickyFilterToggle
          selectedFilters={getSelectedFilters(facets)}
          isSticking={isSticking}
          onClick={e => {
            detachTask(toggleDrawer, e)
            detachTask(sendEvent, {
              engagementDestination: 'go-to_show_filters',
              engagementComponent: 'filter',
              engagementDetails: 'filters_right_show',
              name: 'engagement_filter',
            })
          }}
        />
      </div>
    </div>
  ) : (
    <FilterToggle
      selectedFilters={getSelectedFilters(facets)}
      onClick={e => {
        detachTask(toggleDrawer, e)
        detachTask(sendEvent, {
          engagementDestination: 'go-to_show_filters',
          engagementComponent: 'filter',
          engagementDetails: 'filters_right_show',
          name: 'engagement_filter',
        })
      }}
    />
  )

  useEffect(() => {
    if (containerRef.current && withStickyFilters) {
      const setSticky = async () => {
        const StickyFilter = (await import('./stickyFilter')).default
        // This is needed because after waiting for the import sometimes the ref is null
        if (containerRef.current)
          new StickyFilter(containerRef.current, {
            topSpacing,
            bottomSpacing: 80,
          })
      }

      setSticky()
    }
  }, [containerRef, topSpacing, withStickyFilters])

  return (
    <div className={clsx('Filters', upMd && classes.sidebar)} ref={defaultRef}>
      {isDrawer ? (
        <Drawer
          isSeoFriendly={drawerSeoFrendly && down('sm')}
          anchor="right"
          className={clsx(classes.paper, { [classes.paperPermanent]: !isDrawer })}
          data-testid="filters"
          onClose={e => {
            detachTask(toggleDrawer, e)
            detachTask(sendEvent, {
              engagementDestination: 'go-to_show_filters',
              engagementComponent: 'filter',
              engagementDetails: 'filters_right_hide',
              name: 'engagement_filter',
            })
          }}
          open={isDrawerOpen}
          {...moreProps}
        >
          <RenderFilters
            facets={facets}
            clearCallback={clearCallback}
            rootUrl={rootUrl}
            title={title}
            disableSPA={disableSPA}
            softSPA={softSPA}
            softUpdate={softUpdate}
          />
        </Drawer>
      ) : (
        <Paper square data-testid="filters">
          <RenderFilters
            facets={facets}
            clearCallback={clearCallback}
            rootUrl={rootUrl}
            title={title}
            disableSPA={disableSPA}
            softSPA={softSPA}
            softUpdate={softUpdate}
          />
        </Paper>
      )}
      {isDrawer && filterToggle}
    </div>
  )
}

export default Filters
